import React, { SFC, createContext, useContext, useEffect, useState, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import useFrozen from '../useFrozen';
import useRouter from '../useRouter';
import useListingPageUrlState from '../../components/ListingPage/hooks/useListingPageUrlState';
import { convertUrlToListingPageState } from '../../components/ListingPage/services/listingPageUrlService';
import StateInterface from '../../redux/types';
import usePersistedFlag from './usePersistedFlag';

export interface UserTrackingProps {
    isReturningUser?: boolean;
    isLoggedIn?: boolean;
    didLoginFinishOnce?: boolean;
    isRegistered?: boolean;
}

export const RETURNING_USER_COOKIE_NAME = 'has_user_visited_before';
export const UserTrackingContext = createContext<UserTrackingProps>({ isReturningUser: false });

const { Provider, Consumer } = UserTrackingContext;
export { Consumer as UserTrackingConsumer };

export const UserTrackingProvider: SFC = ({ children }): ReactElement => {
    const [hasVisited, setHasVisited] = usePersistedFlag(RETURNING_USER_COOKIE_NAME);
    const [hasUserNavigated, setHasUserNavigated] = useState(false);
    const { history } = useRouter();
    const [urlState] = useListingPageUrlState();
    const isLoggedIn = useSelector<StateInterface, boolean>(state => state.login.isLoggedIn);
    const didLoginFinishOnce = useSelector<StateInterface, boolean>(state => state.login.didFinishOnce);
    const isRegistered = useSelector<StateInterface, boolean>(state => state.login.isRegistered);

    // Maintain the initial value for this from the first load
    const initialValue = useFrozen(hasVisited);

    useEffect(() => {
        let unlisten = () => {};

        // We do not want to set the cookie on the server because that will mean
        // the client will always get a truthy value
        if (!hasVisited) {
            setHasVisited(1);
        }

        if (!hasUserNavigated) {
            unlisten = history.listen(location => {
                const newUrlState = convertUrlToListingPageState(location.pathname, location.search);

                // We want to de-duplicate filtered pages
                const isSamePath = urlState.listingPageKey === newUrlState.listingPageKey;
                const isSamePage = urlState.page === newUrlState.page;
                const userNavigated = !isSamePath || !isSamePage;

                if (userNavigated) {
                    setHasUserNavigated(true);
                }
            });
        }

        return unlisten;
    }, [hasUserNavigated, hasVisited, history, setHasVisited, urlState.listingPageKey, urlState.page]);

    // Once the user has visited another page she is no more a first-time user
    const isReturningUser = hasUserNavigated ? true : initialValue;

    return <Provider value={{ isReturningUser, isRegistered, isLoggedIn, didLoginFinishOnce }}>{children}</Provider>;
};

const useUserTracking = () => useContext(UserTrackingContext);

export default useUserTracking;
